import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import {
	getFormById,
	createForm,
	updateForm,
	createFormField,
	updateFormField,
	deleteFormField,
	createFormSection,
	updateFormSection,
	deleteFormSection,
	assignFieldToSection,
	reorderFormFields,
	removeFieldFromSectionThunk,
	getFieldTypes,
} from '../../store/formBuilder';
import Card from '../../components/ui/card';
import Button from '../../components/ui/button';
import Tabs from '../../components/ui/tabs';
import Input from '../../components/ui/input';
import Label from '../../components/ui/label';
import {
	FiSave,
	FiArrowLeft,
	FiTrash2,
	FiMove,
	FiX,
	FiSettings,
	FiType,
	FiAlignLeft,
	FiCheckSquare,
	FiList,
	FiCalendar,
	FiMail,
	FiPhone,
	FiLink,
	FiUpload,
	FiCopy,
	FiEdit,
	FiLayers,
	FiFolder,
	FiImage,
} from 'react-icons/fi';
import FormFieldEditor from './FormFieldEditor';
import FormSectionEditor from './FormSectionEditor';
import FormPreview from './FormPreview';
import AccessControlPanel from './components/AccessControlPanel';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus } from 'lucide-react';
import SublyApi from '../../helpers/Api';
import { cn } from '../../lib/utils';

// Option field helpers for the Structure tab
const OPTION_FIELD_TYPES = ['select', 'multiselect', 'radio', 'checkbox'];

const isOptionField = (field) => OPTION_FIELD_TYPES.includes(field.type);

const getFieldOptionsArray = (field) => {
	if (!field.options) return [];
	if (Array.isArray(field.options)) return field.options;
	try {
		return JSON.parse(field.options);
	} catch (e) {
		return [];
	}
};

const FormBuilderEditor = () => {
	const { id } = useParams();
	const history = useHistory();
	const dispatch = useDispatch();
	const isEditMode = Boolean(id);

	// Redux state
	const {
		currentForm,
		formFields,
		formSections,
		fieldSectionMap,
		fieldTypes,
		status,
		error,
	} = useSelector((state) => state.formBuilder);
	const token = useSelector((state) => state.admin.token);

	// Local state
	const [activeTab, setActiveTab] = useState('settings');
	const [formData, setFormData] = useState({
		name: '',
		description: '',
		category: '',
		required_subscription: [],
		is_active: true,
		header_image: '',
		sections: [],
		fields: [],
	});
	const [selectedField, setSelectedField] = useState(null);
	const [headerImageUploading, setHeaderImageUploading] = useState(false);
	const [selectedSection, setSelectedSection] = useState(null);
	const [showFieldEditor, setShowFieldEditor] = useState(false);
	const [showSectionEditor, setShowSectionEditor] = useState(false);
	const [saveMessage, setSaveMessage] = useState('');
	const [saveError, setSaveError] = useState('');
	const headerImageDirtyRef = useRef(false);

	// Effect to load form data when editing
	useEffect(() => {
		if (isEditMode) {
			dispatch(getFormById({ id, token }));
		}

		// Always fetch field types, whether in edit or create mode
		dispatch(getFieldTypes({ token }));
	}, [dispatch, id, isEditMode, token]);

	// Effect to set form data from loaded form
	// Preserve locally-edited fields (like header_image) when formFields change
	useEffect(() => {
		if (currentForm && isEditMode) {
			setFormData((prev) => ({
				name: currentForm.name || '',
				description: currentForm.description || '',
				category: currentForm.category || '',
				required_subscription: currentForm.required_subscription || [],
				is_active:
					currentForm.is_active !== undefined ? currentForm.is_active : true,
				header_image: headerImageDirtyRef.current
					? prev.header_image
					: (currentForm.headerImage ?? currentForm.header_image ?? ''),
				sections: currentForm.sections || [],
				fields: formFields || [],
			}));
		}
	}, [currentForm, isEditMode, formFields]);

	// Handle form input changes
	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target;
		let newValue = type === 'checkbox' ? checked : value;

		setFormData({
			...formData,
			[name]: newValue,
		});
	};

	// Handle header image upload
	const handleHeaderImageUpload = async (file) => {
		const MAX_UPLOAD_SIZE = 10 * 1024 * 1024; // 10MB
		if (file.size > MAX_UPLOAD_SIZE) {
			setSaveError('File must be under 10MB');
			return;
		}
		setHeaderImageUploading(true);
		try {
			const uploadData = new FormData();
			uploadData.append('file', file);
			const response = await SublyApi.request(
				'api/admin/forms/upload-file',
				uploadData,
				'post',
				{
					Authorization: `Bearer ${token}`,
					'Content-Type': 'multipart/form-data',
				}
			);
			headerImageDirtyRef.current = true;
			setFormData((prev) => ({ ...prev, header_image: response.url }));
		} catch (err) {
			console.error('Error uploading header image:', err);
			setSaveError('Failed to upload header image');
		}
		setHeaderImageUploading(false);
	};

	// Remove header image
	const handleRemoveHeaderImage = () => {
		headerImageDirtyRef.current = true;
		setFormData((prev) => ({ ...prev, header_image: '' }));
	};

	// Handle form submission
	const handleSubmit = async (e) => {
		e.preventDefault();
		setSaveMessage('');
		setSaveError('');

		try {
			if (isEditMode) {
	
				const result = await dispatch(
					updateForm({
						id,
						formData,
						token,
					})
				).unwrap();
	
				setSaveMessage('Form settings updated successfully!');

				// Clear success message after 3 seconds
				setTimeout(() => setSaveMessage(''), 3000);
			} else {
	
				const result = await dispatch(
					createForm({
						formData,
						token,
					})
				).unwrap();
	
				// Check if we have a valid ID before redirecting
				if (result.form && result.form.id) {
					setSaveMessage(
						'Form created successfully! Redirecting to edit mode...'
					);
					setTimeout(() => {
						history.replace(`/admin/form-builder/edit/${result.form.id}`);
					}, 1000);
				} else {
					console.error('Form was created but no ID was returned:', result);
					setSaveError(
						'Form was created but there was an issue loading the edit page. Please return to the form list and try again.'
					);
				}
			}
		} catch (error) {
			console.error('Error saving form:', error);
			setSaveError(`Error saving form: ${error.message || 'Unknown error'}`);
		}
	};

	// Add new field
	const handleAddField = () => {
		setSaveError('');
		setSelectedField(null); // Creating a new field
		setShowFieldEditor(true);
	};

	// Edit existing field
	const handleEditField = (field) => {
		setSaveError('');
		setSelectedField(field);
		setShowFieldEditor(true);
	};

	// Save field
	const handleSaveField = (fieldData) => {
		if (!currentForm || !currentForm.id) {
			console.error('No current form available');
			setSaveError('Cannot save field: No form selected');
			return;
		}

		// Duplicate field_key validation
		const existingKeys = (formFields || [])
			.filter(f => !selectedField || f.id !== selectedField.id)
			.map(f => f.field_key);

		if (existingKeys.map(k => k?.toLowerCase()).includes(fieldData.field_key?.toLowerCase())) {
			setSaveError(
				`A field with the key "${fieldData.field_key}" already exists. Please use a different field name.`
			);
			return;
		}

		// Clear any previous save errors
		setSaveError('');

		// Extract section_id from fieldData and remove it from the data we send to update the field
		const sectionId = fieldData.section_id;
		const preparedFieldData = {
			...fieldData,
			form_id: currentForm.id,
		};
		delete preparedFieldData.section_id;

		// If editing existing field
		if (selectedField && selectedField.id) {
			dispatch(
				updateFormField({
					fieldId: selectedField.id,
					field: preparedFieldData,
					token,
				})
			)
				.then((response) => {
					// Handle section assignment after field update
					const currentMapping = fieldSectionMap?.find(
						(mapping) => mapping.field_id === selectedField.id
					);

					if (sectionId) {
						// If section is selected, assign or update section
						if (!currentMapping || currentMapping.section_id !== sectionId) {
							// Remove from current section if exists, then add to new section
							if (currentMapping) {
								handleRemoveFieldFromSection(
									selectedField.id,
									currentMapping.section_id
								).then(() =>
									handleAddFieldToSection(selectedField.id, sectionId)
								);
							} else {
								handleAddFieldToSection(selectedField.id, sectionId);
							}
						}
					} else if (currentMapping) {
						// If no section is selected but field is in a section, remove it
						handleRemoveFieldFromSection(
							selectedField.id,
							currentMapping.section_id
						);
					}

					setShowFieldEditor(false);
					setSelectedField(null);

					// Refresh form data to get updated fields
					dispatch(getFormById({ id: currentForm.id, token }));
				})
				.catch((error) => {
					console.error('Error updating field:', error);
					setSaveError(`Error updating field: ${error.message || 'Unknown error'}`);
				});
		}
		// Creating new field
		else {
			dispatch(
				createFormField({
					formId: currentForm.id,
					field: preparedFieldData,
					token,
				})
			)
				.then((response) => {
					// If a section was selected, assign the new field to it
					if (sectionId) {
						const newFieldId = response.payload?.field?.id;
						if (newFieldId) {
							handleAddFieldToSection(newFieldId, sectionId)
								.then(() => {
									setShowFieldEditor(false);
									setSelectedField(null);
									dispatch(getFormById({ id: currentForm.id, token }));
								})
								.catch((error) => {
									console.error('Error assigning field to section:', error);
								});
						}
					} else {
						setShowFieldEditor(false);
						setSelectedField(null);
						dispatch(getFormById({ id: currentForm.id, token }));
					}
				})
				.catch((error) => {
					console.error('Error creating field:', error);
					setSaveError(`Error creating field: ${error.message || 'Unknown error'}`);
				});
		}
	};

	// Delete field
	const handleDeleteField = async (fieldId) => {
		if (
			window.confirm(
				'Are you sure you want to delete this field? This cannot be undone.'
			)
		) {
			try {
				await dispatch(
					deleteFormField({
						fieldId,
						token,
					})
				).unwrap();
				setSelectedField(null);
				setShowFieldEditor(false);
				setSaveMessage('Field deleted successfully');
				setTimeout(() => setSaveMessage(''), 3000);
			} catch (error) {
				console.error('Error deleting field:', error);
				const message = typeof error === 'string' ? error : (error?.message || 'Unknown error');
				alert(`Failed to delete field: ${message}`);
			}
		}
	};

	// Add new section
	const handleAddSection = () => {
		setSelectedSection(null); // Creating a new section
		setShowSectionEditor(true);
	};

	// Edit existing section
	const handleEditSection = (section) => {
		setSelectedSection(section);
		setShowSectionEditor(true);
	};

	// Save section
	const handleSaveSection = async (sectionData) => {
		try {
			if (!currentForm || !currentForm.id) {
				console.error('No current form available');
				alert('Cannot save section: No form selected');
				return;
			}

			// Calculate position safely
			const position =
				formSections && Array.isArray(formSections) && formSections.length > 0
					? Math.max(...formSections.map((s) => s.position || 0)) + 1
					: 0;

			if (selectedSection) {
				// Update existing section
				await dispatch(
					updateFormSection({
						formId: currentForm.id,
						sectionId: selectedSection.id,
						sectionData,
						token,
					})
				).unwrap();
			} else {
				// Create new section
				const newSection = {
					...sectionData,
					form_id: currentForm.id,
					position: position,
				};

				await dispatch(
					createFormSection({
						formId: currentForm.id,
						sectionData: newSection,
						token,
					})
				).unwrap();
			}

			setShowSectionEditor(false);

			// Refresh form data to get updated sections
			dispatch(getFormById({ id: currentForm.id, token }));
		} catch (error) {
			console.error('Error saving section:', error);
			alert(`Error saving section: ${error.message || 'Unknown error'}`);
		}
	};

	// Delete section
	const handleDeleteSection = async (sectionId) => {
		if (
			window.confirm(
				'Are you sure you want to delete this section? All fields in this section will be removed from the section.'
			)
		) {
			try {
				await dispatch(
					deleteFormSection({
						formId: currentForm.id,
						sectionId,
						token,
					})
				).unwrap();
				setSelectedSection(null);
				setShowSectionEditor(false);
			} catch (error) {
				console.error('Error deleting section:', error);
			}
		}
	};

	// Add field to section
	const handleAddFieldToSection = async (fieldId, sectionId) => {
		try {
			if (!currentForm || !fieldSectionMap) {
				console.error('Current form or field section mapping is not available');
				return;
			}

			const sectionFields = fieldSectionMap
				.filter((mapping) => mapping.section_id === sectionId)
				.sort((a, b) => a.position - b.position);

			const position =
				sectionFields.length > 0
					? Math.max(...sectionFields.map((mapping) => mapping.position)) + 1
					: 0;

			await dispatch(
				assignFieldToSection({
					formId: currentForm.id,
					fieldId,
					sectionId,
					position,
					token,
				})
			).unwrap();
		} catch (error) {
			console.error('Error adding field to section:', error);
		}
	};

	// Remove field from section
	const handleRemoveFieldFromSection = async (fieldId, sectionId) => {
		try {
			if (!fieldSectionMap) {
				console.error('Field section mapping is not available');
				return;
			}

			// Find the mapping to get the field details
			const fieldMapping = fieldSectionMap.find(
				(mapping) =>
					mapping.field_id === fieldId && mapping.section_id === sectionId
			);

			if (!fieldMapping) {
				console.error('Field mapping not found');
				return;
			}

			dispatch(
				removeFieldFromSectionThunk({
					formId: currentForm.id,
					fieldId,
					sectionId,
					token,
				})
			)
				.then(() => {
					dispatch(getFormById({ id: currentForm.id, token }));
				})
				.catch((error) => {
					console.error('Error removing field from section:', error);
				});
		} catch (error) {
			console.error('Error removing field from section:', error);
		}
	};

	// Get fields for a section
	const getFieldsForSection = (sectionId) => {
		if (!formFields) return [];
		return formFields
			.filter((field) => field.section_id === sectionId)
			.sort((a, b) => a.position - b.position);
	};

	// Get unassigned fields (fields not in any section)
	const getUnassignedFields = () => {
		if (!formFields) return [];
		return formFields
			.filter((field) => !field.section_id)
			.sort((a, b) => (a.position || 0) - (b.position || 0));
	};

	// Handle drag and drop reordering
	const handleDragEnd = (result) => {
		if (!result.destination) return;

		const { source, destination, type, draggableId } = result;

		// Handle section reordering
		if (type === 'SECTION') {
			const sections = Array.from(formSections || []);
			const [removed] = sections.splice(source.index, 1);
			sections.splice(destination.index, 0, removed);

			// Update positions
			const updatedSections = sections.map((section, index) => ({
				...section,
				position: index,
			}));

			// Update local state
			setFormData((prev) => ({
				...prev,
				sections: updatedSections,
			}));

			// Persist section order to backend
			if (currentForm && currentForm.id && token) {
				updatedSections.forEach((section) => {
					dispatch(
						updateFormSection({
							formId: currentForm.id,
							sectionId: section.id,
							sectionData: { position: section.position },
							token,
						})
					);
				});
			}
			return;
		}

		// Handle field dragging (type === 'FIELD')
		const sourceDroppableId = source.droppableId;
		const destDroppableId = destination.droppableId;
		const fieldId = parseInt(draggableId.replace('field-', ''), 10);

		// Determine source and destination section IDs
		const sourceSectionId = sourceDroppableId === 'unassigned'
			? null
			: parseInt(sourceDroppableId.replace('section-', ''), 10);
		const destSectionId = destDroppableId === 'unassigned'
			? null
			: parseInt(destDroppableId.replace('section-', ''), 10);

		// Case 1: Moving within the same container (reordering)
		if (sourceDroppableId === destDroppableId) {
			// Get fields for this container
			const containerFields = destSectionId
				? getFieldsForSection(destSectionId)
				: getUnassignedFields();

			const reorderedFields = Array.from(containerFields);
			const [removed] = reorderedFields.splice(source.index, 1);
			reorderedFields.splice(destination.index, 0, removed);

			// Update positions (use 'id' to match backend expectation)
			const fieldPositions = reorderedFields.map((field, index) => ({
				id: field.id,
				position: index,
			}));

			// Persist to backend
			if (currentForm && currentForm.id && token) {
				dispatch(
					reorderFormFields({
						formId: currentForm.id,
						sectionId: destSectionId,
						fieldPositions,
						token,
					})
				)
					.then(() => {
						// Refresh form data
						dispatch(getFormById({ id: currentForm.id, token }));
					})
					.catch((error) => {
						console.error('Error reordering fields:', error);
						dispatch(getFormById({ id: currentForm.id, token }));
					});
			}
			return;
		}

		// Case 2: Moving between containers (changing section assignment)
		// Remove from source section (if it was in a section)
		if (sourceSectionId) {
			dispatch(
				removeFieldFromSectionThunk({
					formId: currentForm.id,
					fieldId,
					sectionId: sourceSectionId,
					token,
				})
			).then(() => {
				// Add to destination section (if not unassigned)
				if (destSectionId) {
					dispatch(
						assignFieldToSection({
							formId: currentForm.id,
							fieldId,
							sectionId: destSectionId,
							position: destination.index,
							token,
						})
					).then(() => {
						dispatch(getFormById({ id: currentForm.id, token }));
					});
				} else {
					dispatch(getFormById({ id: currentForm.id, token }));
				}
			});
		} else {
			// Field was unassigned, just add to destination section
			if (destSectionId) {
				dispatch(
					assignFieldToSection({
						formId: currentForm.id,
						fieldId,
						sectionId: destSectionId,
						position: destination.index,
						token,
					})
				).then(() => {
					dispatch(getFormById({ id: currentForm.id, token }));
				});
			}
		}
	};

	// Add this helper function for field type icons
	const getFieldTypeIcon = (type) => {
		switch (type) {
			case 'text':
				return <FiType className='w-4 h-4' />;
			case 'textarea':
				return <FiAlignLeft className='w-4 h-4' />;
			case 'checkbox':
				return <FiCheckSquare className='w-4 h-4' />;
			case 'select':
			case 'multiselect':
				return <FiList className='w-4 h-4' />;
			case 'date':
				return <FiCalendar className='w-4 h-4' />;
			case 'email':
				return <FiMail className='w-4 h-4' />;
			case 'tel':
				return <FiPhone className='w-4 h-4' />;
			case 'url':
				return <FiLink className='w-4 h-4' />;
			case 'file':
				return <FiUpload className='w-4 h-4' />;
			default:
				return <FiType className='w-4 h-4' />;
		}
	};

	// Duplicate field handler
	const handleDuplicateField = (field) => {
		setSelectedField(null);
		const existingKeys = (formFields || []).map(f => f.field_key?.toLowerCase());
		let copyKey = `${field.field_key}_copy`;
		let copyLabel = `${field.label} (Copy)`;
		let counter = 2;

		while (existingKeys.includes(copyKey?.toLowerCase())) {
			copyKey = `${field.field_key}_copy_${counter}`;
			copyLabel = `${field.label} (Copy ${counter})`;
			counter++;
		}

		const duplicatedField = {
			...field,
			label: copyLabel,
			field_key: copyKey,
		};
		delete duplicatedField.id;
		handleSaveField(duplicatedField);
	};

	// Handle back navigation
	const handleBackToList = () => {
		history.push('/admin/form-builder');
	};

	// Loading state
	if (isEditMode && status === 'loading' && !currentForm) {
		return (
			<div className='flex justify-center items-center h-64'>
				<div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900'></div>
			</div>
		);
	}

	// Error state
	if (error) {
		return (
			<div className='container mx-auto px-4 py-8'>
				<div className='flex items-center mb-6'>
					<button
						onClick={handleBackToList}
						className='mr-4 p-2 hover:bg-gray-100 rounded'>
						<FiArrowLeft className='text-gray-500' />
					</button>
					<h1 className='text-2xl font-bold text-gray-900'>
						{isEditMode ? 'Edit Form' : 'Create Form'}
					</h1>
				</div>

				<div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4'>
					<p className='font-bold'>Error:</p>
					<p>{error}</p>
				</div>

				<Button
					onClick={handleBackToList}
					className='mt-4'>
					Return to Form List
				</Button>
			</div>
		);
	}

	return (
		<div className='container mx-auto px-4 py-8'>
			<div className='flex items-center mb-6'>
				<button
					onClick={handleBackToList}
					className='mr-4 p-2 hover:bg-gray-100 rounded'>
					<FiArrowLeft className='text-gray-500' />
				</button>
				<h1 className='text-2xl font-bold text-gray-900'>
					{isEditMode ? 'Edit Form' : 'Create Form'}
				</h1>
			</div>

			{/* Global Success/Error Messages */}
			{saveMessage && (
				<div className='mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded'>
					{saveMessage}
				</div>
			)}
			{saveError && (
				<div className='mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded'>
					{saveError}
				</div>
			)}

			<Tabs
				defaultValue={activeTab}
				onValueChange={setActiveTab}
				value={activeTab}>
				<Tabs.List className='border-b mb-4'>
					<Tabs.Trigger value='settings'>Settings</Tabs.Trigger>
					<Tabs.Trigger value='structure'>Structure</Tabs.Trigger>
					<Tabs.Trigger value='access'>Access Control</Tabs.Trigger>
					<Tabs.Trigger value='preview'>Preview</Tabs.Trigger>
				</Tabs.List>

				<Tabs.Content value='settings'>
					<Card>
						<Card.Header>
							<h2 className='text-xl font-semibold'>Form Settings</h2>
							<p className='text-sm text-gray-500'>
								Configure basic form information
							</p>
						</Card.Header>
						<Card.Content>

							<form onSubmit={handleSubmit} className='space-y-6'>
								<div>
									<Label htmlFor='name' className='required'>
										Form Name
									</Label>
									<Input
										id='name'
										name='name'
										value={formData.name}
										onChange={handleInputChange}
										placeholder='Enter form name'
										required
									/>
								</div>

								<div>
									<Label htmlFor='description'>Description</Label>
									<textarea
										id='description'
										name='description'
										value={formData.description}
										onChange={handleInputChange}
										placeholder='Enter form description'
										rows={3}
										className='w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
									/>
								</div>

								<div>
									<Label htmlFor='category'>Category</Label>
									<Input
										id='category'
										name='category'
										value={formData.category}
										onChange={handleInputChange}
										placeholder='Enter form category'
									/>
									<p className='text-sm text-gray-500 mt-1'>
										Used to organize and group similar forms
									</p>
								</div>

								{/* Header Image Upload */}
								<div>
									<Label className='flex items-center gap-2'>
										<FiImage className='h-4 w-4' />
										Header Image
									</Label>
									<p className='text-sm text-gray-500 mt-1 mb-3'>
										Displayed as a banner at the top of the form
									</p>
									{headerImageUploading ? (
										<div className='flex items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50'>
											<div className='flex items-center gap-2 text-gray-400'>
												<div className='w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin' />
												<span className='text-sm'>Uploading...</span>
											</div>
										</div>
									) : formData.header_image ? (
										<div className='relative group'>
											<img
												src={formData.header_image}
												alt='Form header'
												className='w-full h-32 object-cover rounded-lg border border-gray-200'
											/>
											<button
												type='button'
												onClick={handleRemoveHeaderImage}
												className='absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-md'>
												<FiX className='h-4 w-4' />
											</button>
										</div>
									) : (
										<label className='flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 cursor-pointer hover:border-gray-400 hover:bg-gray-100 transition-all duration-200'>
											<FiImage className='h-8 w-8 text-gray-400 mb-2' />
											<span className='text-sm text-gray-500'>Click to upload header image</span>
											<span className='text-xs text-gray-400 mt-1'>Recommended: 1200x400px</span>
											<input
												type='file'
												accept='image/*'
												className='sr-only'
												onChange={(e) => {
													const file = e.target.files?.[0];
													if (file) handleHeaderImageUpload(file);
													e.target.value = '';
												}}
											/>
										</label>
									)}
								</div>

								<div className='flex items-center space-x-2'>
									<input
										type='checkbox'
										id='is_active'
										name='is_active'
										checked={formData.is_active}
										onChange={handleInputChange}
										className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
									/>
									<Label htmlFor='is_active'>Active Form</Label>
									<p className='text-sm text-gray-500'>
										Only active forms are visible to users
									</p>
								</div>

								<div className='flex items-center space-x-4 pt-4 border-t'>
									<Button
										type='submit'
										disabled={status === 'loading'}
										className='bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap'>
										{status === 'loading' ? (
											<>
												<div className='animate-spin h-4 w-4 border border-white border-t-transparent rounded-full mr-2'></div>
												Saving...
											</>
										) : (
											<>
												<FiSave className='h-4 w-4 mr-2' />
												{isEditMode ? 'Update Form' : 'Create Form'}
											</>
										)}
									</Button>

									{isEditMode && (
										<p className='text-sm text-gray-500'>
											Changes to form settings will be saved immediately
										</p>
									)}
								</div>
							</form>
						</Card.Content>
					</Card>
				</Tabs.Content>

				<Tabs.Content value='structure'>
					<Card>
						<Card.Header>
							<div className='flex items-center justify-between'>
								<div>
									<h2 className='text-xl font-semibold'>Form Structure</h2>
									<p className='text-sm text-gray-500 mt-1'>
										Drag fields between sections to organize your form. Drag section headers to reorder sections.
									</p>
								</div>
								<div className='flex space-x-2'>
									<Button
										variant='outline'
										onClick={handleAddField}
										className='flex items-center'>
										<Plus className='w-4 h-4 mr-2' />
										Add Field
									</Button>
									<Button
										variant='outline'
										onClick={handleAddSection}
										className='flex items-center'>
										<FiFolder className='w-4 h-4 mr-2' />
										Add Section
									</Button>
								</div>
							</div>
						</Card.Header>
						<Card.Content>
							{saveError && (
								<div className='mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded'>
									{saveError}
								</div>
							)}
							<DragDropContext onDragEnd={handleDragEnd}>
								{/* Sections Container - Droppable for section reordering */}
								<Droppable droppableId='sections-container' type='SECTION'>
									{(sectionsProvided) => (
										<div
											ref={sectionsProvided.innerRef}
											{...sectionsProvided.droppableProps}
											className='space-y-4'>
											{/* Render each section as a draggable with droppable field container */}
											{[...(formSections || [])]
												.sort((a, b) => (a.position || 0) - (b.position || 0))
												.map((section, sectionIndex) => {
													const sectionFields = getFieldsForSection(section.id);
													return (
														<Draggable
															key={`section-${section.id}`}
															draggableId={`section-${section.id}`}
															index={sectionIndex}>
															{(sectionProvided, sectionSnapshot) => (
																<div
																	ref={sectionProvided.innerRef}
																	{...sectionProvided.draggableProps}
																	className={cn(
																		'border rounded-lg overflow-hidden bg-white',
																		sectionSnapshot.isDragging && 'shadow-lg ring-2 ring-blue-400'
																	)}>
																	{/* Section Header - Drag Handle */}
																	<div
																		{...sectionProvided.dragHandleProps}
																		className='flex items-center justify-between bg-gray-50 p-3 border-b cursor-move hover:bg-gray-100 transition-colors'>
																		<div className='flex items-center space-x-2'>
																			<FiMove className='text-gray-400' />
																			<FiFolder className='text-gray-500' />
																			<span className='font-medium'>
																				{section.title || 'Untitled Section'}
																			</span>
																			<span className='text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded'>
																				{sectionFields.length} field{sectionFields.length !== 1 ? 's' : ''}
																			</span>
																		</div>
																		<div className='flex space-x-1'>
																			<button
																				onClick={(e) => {
																					e.stopPropagation();
																					handleEditSection(section);
																				}}
																				className='p-1.5 hover:bg-gray-200 rounded'
																				title='Edit section'>
																				<FiSettings className='w-4 h-4 text-gray-500' />
																			</button>
																			<button
																				onClick={(e) => {
																					e.stopPropagation();
																					handleDeleteSection(section.id);
																				}}
																				className='p-1.5 hover:bg-red-100 rounded'
																				title='Delete section'>
																				<FiTrash2 className='w-4 h-4 text-red-500' />
																			</button>
																		</div>
																	</div>

																	{/* Section Description */}
																	{section.description && (
																		<div className='px-4 py-2 bg-gray-50 border-b'>
																			<p className='text-sm text-gray-500'>{section.description}</p>
																		</div>
																	)}

																	{/* Fields Droppable Area */}
																	<Droppable droppableId={`section-${section.id}`} type='FIELD'>
																		{(fieldsProvided, fieldsSnapshot) => (
																			<div
																				ref={fieldsProvided.innerRef}
																				{...fieldsProvided.droppableProps}
																				className={cn(
																					'p-3 min-h-[80px] transition-colors',
																					fieldsSnapshot.isDraggingOver && 'bg-blue-50'
																				)}>
																				{sectionFields.length === 0 ? (
																					<div className='p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 text-center'>
																						<FiLayers className='w-6 h-6 text-gray-300 mx-auto mb-2' />
																						<p className='text-gray-400 text-sm'>
																							Drag fields here
																						</p>
																					</div>
																				) : (
																					<div className='space-y-2'>
																						{sectionFields.map((field, fieldIndex) => (
																							<Draggable
																								key={`field-${field.id}`}
																								draggableId={`field-${field.id}`}
																								index={fieldIndex}>
																								{(fieldProvided, fieldSnapshot) => {
																									const fieldOptions = isOptionField(field) ? getFieldOptionsArray(field) : [];
																									const imageOptions = fieldOptions.filter((opt) => opt.imageUrl);
																									return (
																									<div
																										ref={fieldProvided.innerRef}
																										{...fieldProvided.draggableProps}
																										className={cn(
																											'group flex flex-col bg-white border border-gray-200 rounded-lg transition-all duration-200',
																											'hover:border-black/20 hover:shadow-[0_2px_12px_rgba(204,29,185,0.07)]',
																											fieldSnapshot.isDragging && 'shadow-[0_8px_32px_rgba(204,29,185,0.16)] border-[#CC1DB9] scale-[1.01]'
																										)}>
																										{/* Main content row */}
																										<div className='flex items-center px-3 py-3'>
																											<div
																												{...fieldProvided.dragHandleProps}
																												className='p-1.5 mr-2 rounded hover:bg-gray-100 cursor-grab active:cursor-grabbing'>
																												<FiMove className='w-4 h-4 text-gray-400' />
																											</div>
																											<span className='text-gray-400 mr-2 group-hover:text-[#CC1DB9] transition-colors duration-200 shrink-0'>
																												{getFieldTypeIcon(field.type)}
																											</span>
																											<div className='flex-1 min-w-0'>
																												<h4 className='font-glacial text-sm text-[#202020] truncate'>{field.label}</h4>
																												<span className='font-montserrat-medium text-[10px] uppercase tracking-widest text-gray-500'>{field.type}</span>
																											</div>
																											<div className='hidden sm:flex items-center space-x-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150 ml-2 shrink-0'>
																												<button
																													onClick={() => handleEditField(field)}
																													className='p-1.5 rounded hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-[#CC1DB9] focus-visible:ring-offset-1'
																													title='Edit field'
																													aria-label={`Edit field: ${field.label}`}>
																													<FiEdit className='w-3.5 h-3.5 text-gray-500' />
																												</button>
																												<button
																													onClick={() => handleRemoveFieldFromSection(field.id, section.id)}
																													className='p-1.5 rounded hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-[#CC1DB9] focus-visible:ring-offset-1'
																													title='Move to unassigned'
																													aria-label={`Move to unassigned: ${field.label}`}>
																													<FiX className='w-3.5 h-3.5 text-gray-500' />
																												</button>
																												<button
																													onClick={() => handleDeleteField(field.id)}
																													className='p-1.5 rounded hover:bg-red-100 focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-1'
																													title='Delete field'
																													aria-label={`Delete field: ${field.label}`}>
																													<FiTrash2 className='w-3.5 h-3.5 text-red-500' />
																												</button>
																											</div>
																										</div>
																										{/* Metadata row — option fields with options only */}
																										{fieldOptions.length > 0 && (
																											<div className='flex flex-wrap items-center gap-1.5 px-3 pb-2.5'>
																												<span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-montserrat-medium bg-gray-100 text-gray-500 border border-gray-200 shrink-0'>
																													<FiList className='w-2.5 h-2.5 shrink-0' />
																													{fieldOptions.length} option{fieldOptions.length !== 1 ? 's' : ''}
																												</span>
																												{imageOptions.length > 0 && (
																													<>
																													<span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-montserrat-medium bg-[#CC1DB9]/8 text-[#CC1DB9] border border-[#CC1DB9]/20 shrink-0'>
																														<FiImage className='w-2.5 h-2.5 shrink-0' />
																														<span className='hidden sm:inline'>images</span>
																													</span>
																													<div className='flex items-center shrink-0'>
																														{imageOptions.slice(0, 3).map((opt, i) => (
																															<img
																																key={i}
																																src={opt.imageUrl}
																																alt={opt.label}
																																className='w-6 h-6 rounded object-cover border-2 border-white ring-1 ring-gray-200 -ml-1 first:ml-0'
																															/>
																														))}
																														{imageOptions.length > 3 && (
																															<span className='w-6 h-6 rounded bg-gray-100 border-2 border-white ring-1 ring-gray-200 -ml-1 flex items-center justify-center text-[8px] font-montserrat-medium text-gray-500'>
																																+{imageOptions.length - 3}
																															</span>
																														)}
																													</div>
																													</>
																												)}
																											</div>
																										)}
																									</div>
																									);
																								}}
																							</Draggable>
																						))}
																					</div>
																				)}
																				{fieldsProvided.placeholder}
																			</div>
																		)}
																	</Droppable>
																</div>
															)}
														</Draggable>
													);
												})}
											{sectionsProvided.placeholder}
										</div>
									)}
								</Droppable>

								{/* Unassigned Fields Section */}
								<div className='mt-6'>
									<div className='flex items-center space-x-2 mb-3'>
										<FiLayers className='text-gray-400' />
										<h3 className='font-medium text-gray-700'>Unassigned Fields</h3>
										<span className='text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded'>
											{getUnassignedFields().length} field{getUnassignedFields().length !== 1 ? 's' : ''}
										</span>
									</div>
									<p className='text-sm text-gray-500 mb-3'>
										Fields not in any section. Drag them into a section above, or they will appear in an "Additional Information" section in the form.
									</p>
									<Droppable droppableId='unassigned' type='FIELD'>
										{(unassignedProvided, unassignedSnapshot) => (
											<div
												ref={unassignedProvided.innerRef}
												{...unassignedProvided.droppableProps}
												className={cn(
													'p-3 min-h-[100px] border-2 border-dashed rounded-lg transition-colors',
													unassignedSnapshot.isDraggingOver
														? 'border-blue-400 bg-blue-50'
														: 'border-gray-300 bg-gray-50'
												)}>
												{getUnassignedFields().length === 0 ? (
													<div className='flex flex-col items-center justify-center py-6 text-gray-400'>
														<FiLayers className='w-8 h-8 mb-2' />
														<p className='text-sm'>No unassigned fields</p>
														<p className='text-xs mt-1'>All fields are organized in sections</p>
													</div>
												) : (
													<div className='space-y-2'>
														{getUnassignedFields().map((field, index) => (
															<Draggable
																key={`field-${field.id}`}
																draggableId={`field-${field.id}`}
																index={index}>
																{(fieldProvided, fieldSnapshot) => {
																	const fieldOptions = isOptionField(field) ? getFieldOptionsArray(field) : [];
																	const imageOptions = fieldOptions.filter((opt) => opt.imageUrl);
																	return (
																	<div
																		ref={fieldProvided.innerRef}
																		{...fieldProvided.draggableProps}
																		className={cn(
																			'group flex flex-col bg-white border border-gray-200 rounded-lg transition-all duration-200',
																			'hover:border-black/20 hover:shadow-[0_2px_12px_rgba(204,29,185,0.07)]',
																			fieldSnapshot.isDragging && 'shadow-[0_8px_32px_rgba(204,29,185,0.16)] border-[#CC1DB9] scale-[1.01]'
																		)}>
																		{/* Main content row */}
																		<div className='flex items-center px-3 py-3'>
																			<div
																				{...fieldProvided.dragHandleProps}
																				className='p-1.5 mr-2 rounded hover:bg-gray-100 cursor-grab active:cursor-grabbing'>
																				<FiMove className='w-4 h-4 text-gray-400' />
																			</div>
																			<span className='text-gray-400 mr-2 group-hover:text-[#CC1DB9] transition-colors duration-200 shrink-0'>
																				{getFieldTypeIcon(field.type)}
																			</span>
																			<div className='flex-1 min-w-0'>
																				<h4 className='font-glacial text-sm text-[#202020] truncate'>{field.label}</h4>
																				<span className='font-montserrat-medium text-[10px] uppercase tracking-widest text-gray-500'>{field.type}</span>
																			</div>
																			<div className='hidden sm:flex items-center space-x-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150 ml-2 shrink-0'>
																				<button
																					onClick={() => handleDuplicateField(field)}
																					className='p-1.5 rounded hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-[#CC1DB9] focus-visible:ring-offset-1'
																					title='Duplicate field'
																					aria-label={`Duplicate field: ${field.label}`}>
																					<FiCopy className='w-3.5 h-3.5 text-gray-500' />
																				</button>
																				<button
																					onClick={() => handleEditField(field)}
																					className='p-1.5 rounded hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-[#CC1DB9] focus-visible:ring-offset-1'
																					title='Edit field'
																					aria-label={`Edit field: ${field.label}`}>
																					<FiEdit className='w-3.5 h-3.5 text-gray-500' />
																				</button>
																				<button
																					onClick={() => handleDeleteField(field.id)}
																					className='p-1.5 rounded hover:bg-red-100 focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-1'
																					title='Delete field'
																					aria-label={`Delete field: ${field.label}`}>
																					<FiTrash2 className='w-3.5 h-3.5 text-red-500' />
																				</button>
																			</div>
																		</div>
																		{/* Metadata row — option fields with options only */}
																		{fieldOptions.length > 0 && (
																			<div className='flex flex-wrap items-center gap-1.5 px-3 pb-2.5'>
																				<span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-montserrat-medium bg-gray-100 text-gray-500 border border-gray-200 shrink-0'>
																					<FiList className='w-2.5 h-2.5' />
																					{fieldOptions.length} option{fieldOptions.length !== 1 ? 's' : ''}
																				</span>
																				{imageOptions.length > 0 && (
																					<>
																					<span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-montserrat-medium bg-[#CC1DB9]/8 text-[#CC1DB9] border border-[#CC1DB9]/20 shrink-0'>
																						<FiImage className='w-2.5 h-2.5' />
																						<span className='hidden sm:inline'>images</span>
																					</span>
																					<div className='flex items-center shrink-0'>
																						{imageOptions.slice(0, 3).map((opt, i) => (
																							<img
																								key={i}
																								src={opt.imageUrl}
																								alt={opt.label}
																								className='w-6 h-6 rounded object-cover border-2 border-white ring-1 ring-gray-200 -ml-1 first:ml-0'
																							/>
																						))}
																						{imageOptions.length > 3 && (
																							<span className='w-6 h-6 rounded bg-gray-100 border-2 border-white ring-1 ring-gray-200 -ml-1 flex items-center justify-center text-[8px] font-montserrat-medium text-gray-500'>
																								+{imageOptions.length - 3}
																							</span>
																						)}
																					</div>
																					</>
																				)}
																			</div>
																		)}
																	</div>
																	);
																}}
															</Draggable>
														))}
													</div>
												)}
												{unassignedProvided.placeholder}
											</div>
										)}
									</Droppable>
								</div>
							</DragDropContext>
						</Card.Content>
					</Card>
				</Tabs.Content>

				<Tabs.Content value='access'>
					<AccessControlPanel
						formId={currentForm?.id}
						initialAccessSettings={{
							universalAccess: currentForm?.universal_access || false,
							allowedLookupKeys: currentForm?.allowed_lookup_keys || [],
						}}
						token={token}
						onSave={() => {}}
					/>
				</Tabs.Content>

				<Tabs.Content value='preview'>
					<Card>
						<Card.Header>
							<h2 className='text-xl font-semibold'>Form Preview</h2>
							<p className='text-sm text-gray-500'>
								This is how your form will appear to users
							</p>
						</Card.Header>
						<Card.Content>
							{currentForm && formFields ? (
								<FormPreview
									form={{
										...currentForm,
										headerImage: formData.header_image || currentForm.headerImage || '',
										fields: formFields,
										sections: formSections || [],
									}}
									sections={formSections || []}
									fields={formFields}
									fieldSectionMap={fieldSectionMap || []}
									isPreview={true}
								/>
							) : (
								<div className='text-center py-8 text-gray-500'>
									<p>Add some fields to see how your form will look</p>
								</div>
							)}
						</Card.Content>
					</Card>
				</Tabs.Content>
			</Tabs>

			{/* Field Editor Modal */}
			{showFieldEditor && (
				<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
					<div className='bg-white rounded-lg shadow-lg max-w-2xl w-full'>
						<FormFieldEditor
							field={selectedField}
							sections={formSections}
							fieldTypes={fieldTypes}
							existingFieldKeys={
								(formFields || [])
									.filter(f => !selectedField || f.id !== selectedField.id)
									.map(f => f.field_key)
							}
							onSave={handleSaveField}
							onCancel={() => { setShowFieldEditor(false); setSelectedField(null); }}
							onDelete={
								selectedField ? () => handleDeleteField(selectedField.id) : null
							}
						/>
					</div>
				</div>
			)}

			{/* Section Editor Modal */}
			{showSectionEditor && (
				<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
					<div className='bg-white p-6 rounded-lg shadow-lg max-w-md w-full'>
						<FormSectionEditor
							section={selectedSection}
							onSave={handleSaveSection}
							onCancel={() => setShowSectionEditor(false)}
							onDelete={
								selectedSection
									? () => handleDeleteSection(selectedSection.id)
									: null
							}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export default FormBuilderEditor;
